import { baseConfig, useOidcStore } from "@scayla/core";

import { $t } from "@/i18n";

async function requestSignedFileUrl(objectName: string, generation?: string): Promise<Response | undefined> {
  const token = useOidcStore().state.value.token;
  if (!token) return undefined;

  const query = generation ? `?generation=${encodeURIComponent(generation)}` : "";
  return fetch(`${baseConfig.scaylaApiUrl}/files/transfer/signed_url/${encodeURIComponent(objectName)}${query}`, {
    headers: { authorization: `Bearer ${token}` },
    method: "HEAD",
  });
}

/**
 * Short-lived download URL of a stored file, or `undefined` when there is no session or the
 * backend returned no `signed-url` header. `generation` selects an older version of the file.
 */
export async function fetchSignedFileUrl(objectName: string, generation?: string): Promise<string | undefined> {
  const response = await requestSignedFileUrl(objectName, generation);
  return response?.headers.get("signed-url") ?? undefined;
}

/**
 * Like `fetchSignedFileUrl`, but throws a user-facing error instead of returning `undefined`: "not
 * found" when the file or its stored content is gone, the download error otherwise.
 */
export async function fetchSignedFileUrlOrThrow(objectName: string, generation?: string): Promise<string> {
  const response = await requestSignedFileUrl(objectName, generation).catch(() => undefined);
  const signedUrl = response?.headers.get("signed-url");
  if (signedUrl) return signedUrl;
  throw new Error(
    response?.status === 404 ? $t("common.message.fileNotFound") : $t("common.message.downloadFileError"),
  );
}
